import { Content, PageWithHeader } from '@backstage/core-components';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import GpsFixedIcon from '@material-ui/icons/GpsFixed';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import NorthIcon from '@material-ui/icons/North';
import SecurityIcon from '@material-ui/icons/Security';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DevxBreadCrumb } from '../common/BreadcrumbsNav/DevxBreadCrumb';
import BackButton from '../utils/backButton';

const GUIDE_ITEMS = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'creating-agents', label: 'Creating Agents' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'using-agents', label: 'Using Agents' },
  { id: 'best-practices', label: 'Best Practices' },
  { id: 'faq', label: 'FAQ' },
];

const BENEFIT_CARDS = [
  {
    icon: FlashOnIcon,
    title: 'Speed up routine work',
    description:
      'Automate repetitive, time-intensive tasks so your team can focus on higher-value activities.',
  },
  {
    icon: GpsFixedIcon,
    title: 'Purpose-built accuracy',
    description:
      'Each agent is scoped to a specific task, producing more relevant and reliable outputs than a generic tool.',
  },
  {
    icon: VerifiedUserIcon,
    title: 'Built by CBRE, for CBRE',
    description:
      "Agents are created by colleagues who understand CBRE's processes, terminology, and standards.",
  },
  {
    icon: SecurityIcon,
    title: 'Governance & trust',
    description:
      'Every published agent goes through an approval process to ensure quality, compliance, and data safety.',
  },
];

const BASIC_INFORMATION_ROWS = [
  [
    'Agent Name',
    'REQUIRED',
    'A clear, descriptive name for your agent (e.g. "Proposal Drafting Agent")',
  ],
  [
    'Description',
    'REQUIRED',
    'A concise summary of what the agent does and who it is for. The avatar initials are generated automatically from this name.',
  ],
  [
    'Segment',
    'REQUIRED',
    'The CBRE business segment this agent belongs to (select from the dropdown)',
  ],
];

const CLASSIFICATION_ROWS = [
  [
    'AI Category (Agent Type)',
    'REQUIRED',
    "Select the type of AI model or approach used (e.g. generative AI, classification, extraction). This helps users understand the agent's capabilities and limitations.",
  ],
];

const OVERVIEW_ROWS = [
  [
    'Overview',
    'OPTIONAL',
    'A longer narrative description of the agent, including its purpose, context, and key benefits.',
  ],
  [
    'Capability 1-4',
    'OPTIONAL',
    'List up to 4 specific things this agent can do. Be concise and action-oriented (e.g. "Generates first drafts of client proposals from a brief").',
  ],
];

const TARGET_AUDIENCE_ROWS = [
  [
    'Designed For',
    'OPTIONAL',
    'Describe the intended user (e.g. "Leasing Advisors", "Finance Analysts", "All employees")',
  ],
  [
    'Business Group',
    'OPTIONAL',
    'The CBRE team or department this is primarily built for',
  ],
  [
    'Geography',
    'OPTIONAL',
    'Select the geographic scope: global, regional, or specific country',
  ],
  [
    'Specific Location',
    'OPTIONAL',
    'If applicable, enter a specific city or office location',
  ],
];

const INPUT_OUTPUT_ROWS = [
  [
    'What You Provide (Items 1-4)',
    'OPTIONAL',
    'List the inputs users must supply: documents, data, prompts, forms, etc. (up to 4 items)',
  ],
  [
    'What You Get (Items 1-4)',
    'OPTIONAL',
    'List the outputs users can expect: a drafted document, a summary, a data table, a recommendation, etc. (up to 4 items)',
  ],
];

const GOVERNANCE_ROWS = [
  [
    'Data Access',
    'OPTIONAL',
    'Describe what data the assistant accesses or processes (e.g. "Reads uploaded lease documents — no PII stored")',
  ],
  [
    'Compliance',
    'OPTIONAL',
    'Note any compliance standards or approvals this assistant meets (e.g. GDPR, CBRE data policy)',
  ],
  [
    'Validation Summary',
    'OPTIONAL',
    'Briefly describe how the assistant was tested and validated before submission',
  ],
];

const EVIDENCE_ROWS = [
  [
    'Upload Evidence File',
    'OPTIONAL',
    "Upload a PDF, JPG, or PNG file that demonstrates the assistant's outputs or validation results",
  ],
  [
    'Evidence Link',
    'OPTIONAL',
    'Alternatively, provide a URL to a shared document, SharePoint page, or Confluence page',
  ],
  ['Validated By', 'OPTIONAL', 'Name of the person or team that validated the assistant'],
  ['Version', 'OPTIONAL', 'The version of the assistant being submitted (defaults to v1.0)'],
  [
    'Important to Know',
    'OPTIONAL',
    'Pre-populated with a standard disclaimer. Edit this to add any specific warnings or limitations users should be aware of.',
  ],
];

const RELEASE_NOTE_ROWS = [
  ['Version', '-', 'The version number these notes apply to (e.g. v1.0, v1.1)'],
  ['Note 1-4', '-', 'Describe what is new or improved in this version (up to 4 notes per version)'],
  ['+ Add Version', '-', 'Click to add release notes for additional versions'],
];

const PROMPTING_TIPS = [
  ['Clear and specific', 'Explain exactly what you need'],
  ['Contextual', 'Provide background information and relevant details'],
  ['Well-formatted', 'Use structure to help the agent understand your request'],
  ['Iterative', 'Refine your prompts based on results'],
];

const QA_ITEMS = [
  'Accuracy and relevance',
  'Compliance with organizational standards',
  'Missing or incorrect information',
  'Tone and appropriateness',
];

const BEST_PRACTICES = [
  [
    '1. Start Small and Scale',
    'Begin with a specific, well-defined use case. Prove value with your team, then expand to other departments. This approach reduces risk and builds organizational confidence in AI tools.',
  ],
  [
    '2. Maintain Human Oversight',
    'AI agents are tools that augment human work, not replace it. Always maintain appropriate oversight and decision-making authority, especially for high-impact decisions.',
  ],
  [
    '3. Invest in Training',
    'Train your team on how to use agents effectively. Better training leads to higher adoption and better results.',
  ],
  [
    '4. Document Workflows',
    'Create documentation showing how your team uses each agent. This helps with consistency, training, and knowledge sharing across the organization.',
  ],
  [
    '5. Monitor Performance',
    "Regularly review how agents are performing. Track metrics like usage, feedback, and business impact to ensure they're delivering value.",
  ],
  [
    '6. Share Knowledge',
    'Share successful use cases and lessons learned with other teams. Community learning accelerates adoption and drives better outcomes across CBRE.',
  ],
];

const FAQ_ITEMS = [
  [
    'Do I need to be a developer to create an agent?',
    'No. The catalog is open to all CBRE employees. You describe your agent using the form fields — no coding is required to submit. Technical details about how the AI is built may be captured in the governance fields if relevant.',
  ],
  [
    'What is the difference between a Personal, Team, and Enterprise Agent?',
    'Personal agents are created for your own use. Team agents are shared within a specific group or department. Enterprise agents are available to the broader CBRE organization once approved.',
  ],
  [
    'Can I edit my agent after submitting it?',
    'You can save a draft and edit it at any time before submission. Once submitted for review, edits are locked until the review is complete. After approval, you can create a new version and resubmit.',
  ],
  [
    'What counts as valid evidence?',
    "Evidence should demonstrate that your agent works as described and meets CBRE's quality and compliance standards. Examples include: screenshots of test outputs, a validation report, a link to a test log in SharePoint or Confluence, or a document showing stakeholder sign-off.",
  ],
  [
    'Who can I contact for help?',
    'If you have questions about the catalog or the approval process, visit the Help page/FAQ link in the governance section of the form, or contact the catalog support team at support@example.com.',
  ],
];

const useStyles = makeStyles((theme: Theme) => ({
  topAnchor: {
    position: 'relative',
    top: -16,
  },
  content: {
    width: '100%',
  },
  container: {
    width: '100%',
    maxWidth: 1320,
    margin: '0 auto',
    padding: '12px 16px 24px',
  },
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    gap: 16,
  },
  guideLayout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 56,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: 28,
    },
  },
  sidebar: {
    width: 246,
    flexShrink: 0,
    alignSelf: 'flex-start',
    borderLeft: '4px solid #0b5d4a',
    paddingLeft: 16,
    position: 'sticky',
    top: 32,
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      position: 'static',
      maxHeight: 'none',
      overflowY: 'visible',
    },
  },
  sidebarTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    marginBottom: 18,
  },
  navList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  navItem: {
    display: 'block',
    textDecoration: 'none',
    color: '#4c4f52',
    fontSize: 14,
    fontWeight: 500,
    padding: '11px 14px',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f3f5f4',
      color: theme.palette.text.secondary,
    },
  },
  activeNavItem: {
    backgroundColor: '#f0f2f1',
    color: '#0b5d4a',
    fontWeight: 700,
  },
  main: {
    flex: 1,
    minWidth: 0,
    maxWidth: 940,
  },
  section: {
    marginBottom: 88,
    scrollMarginTop: 32,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    color: '#0b5d4a',
    marginBottom: 10,
  },
  title: {
    fontSize: 32,
    fontWeight: 800,
    lineHeight: 1.15,
    color: '#222222',
    marginBottom: 18,
    [theme.breakpoints.down('sm')]: {
      fontSize: 28,
    },
  },
  lead: {
    fontSize: 16,
    lineHeight: 1.9,
    color: '#55585c',
    marginBottom: 20,
  },
  sectionHeading: {
    fontSize: 20,
    fontWeight: 800,
    lineHeight: 1.3,
    color: '#0b5d4a',
    marginBottom: 10,
    marginTop: 28,
  },
  sectionParagraph: {
    fontSize: 15,
    lineHeight: 1.9,
    color: '#55585c',
    marginBottom: 18,
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 24,
    marginTop: 34,
    marginBottom: 42,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  benefitCard: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border}`,
    borderRadius: 12,
    padding: 24,
    minHeight: 182,
  },
  benefitIconBox: {
    width: 46,
    height: 46,
    borderRadius: 10,
    backgroundColor: '#edf2f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0b5d4a',
    marginBottom: 18,
    '& svg': {
      fontSize: 22,
    },
  },
  benefitTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: '#2a2d2f',
    marginBottom: 12,
  },
  benefitText: {
    fontSize: 14,
    lineHeight: 1.8,
    color: '#66696d',
  },
  callout: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    backgroundColor: '#eff9f6',
    borderLeft: '4px solid #0b5d4a',
    padding: '18px 20px',
    marginTop: 30,
  },
  calloutIcon: {
    color: '#0b5d4a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  calloutText: {
    fontSize: 14,
    lineHeight: 1.8,
    color: '#233234',
    '& strong': {
      fontWeight: 800,
    },
  },
  tableBlock: {
    border: `1px solid ${theme.palette.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    marginTop: 14,
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '34% 12% 54%',
    backgroundColor: theme.palette.background.paper,
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.border}`,
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  tableCell: {
    padding: '18px 16px',
    fontSize: 14,
    lineHeight: 1.7,
    color: '#4f5357',
    '&:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.border}`,
    },
    [theme.breakpoints.down('sm')]: {
      '&:not(:last-child)': {
        borderRight: 0,
        borderBottom: `1px solid ${theme.palette.border}`,
      },
    },
  },
  fieldName: {
    fontWeight: 700,
    color: '#222222',
  },
  statusCell: {
    fontWeight: 800,
    color: '#0b5d4a',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'left',
    },
  },
  promptList: {
    marginTop: 14,
    marginBottom: 26,
  },
  promptItem: {
    fontSize: 15,
    lineHeight: 2.4,
    color: '#4f5357',
    '& strong': {
      color: '#222222',
      fontWeight: 800,
    },
  },
  qaList: {
    marginTop: 6,
    marginBottom: 26,
  },
  qaItem: {
    fontSize: 15,
    lineHeight: 2.4,
    color: '#4f5357',
  },
  practiceItem: {
    marginBottom: 24,
  },
  practiceTitle: {
    fontSize: 20,
    fontWeight: 800,
    color: '#0b5d4a',
    marginBottom: 10,
  },
  practiceText: {
    fontSize: 15,
    lineHeight: 1.9,
    color: '#55585c',
  },
  faqList: {
    borderTop: `1px solid ${theme.palette.border}`,
    marginTop: 26,
  },
  faqItem: {
    padding: '30px 0',
    borderBottom: `1px solid ${theme.palette.border}`,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.5,
    color: '#222222',
    marginBottom: 14,
  },
  faqAnswer: {
    fontSize: 15,
    lineHeight: 1.9,
    color: '#55585c',
  },
  backToTopRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 18,
  },
  backToTop: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 14,
    fontWeight: 700,
    color: '#0b5d4a',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
    '& svg': {
      fontSize: 16,
    },
  },
}));

const AiStoreGuidePage = () => {
  const classes = useStyles();
  const location = useLocation();
  const [activeSectionId, setActiveSectionId] = useState(
    location.hash.replace('#', '') || 'getting-started',
  );

  useEffect(() => {
    const sectionId = location.hash.replace('#', '') || 'getting-started';
    const sectionElement = document.getElementById(sectionId);

    setActiveSectionId(sectionId);

    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  useEffect(() => {
    const sectionElements = GUIDE_ITEMS.map(item => document.getElementById(item.id)).filter(
      (element): element is HTMLElement => Boolean(element),
    );

    if (!sectionElements.length) {
      return;
    }

    const observedSections = new Map<string, IntersectionObserverEntry>();

    const getFallbackSectionId = () => {
      const viewportLine = Math.max(window.innerHeight * 0.35, 220);
      const closestSection = sectionElements.reduce((closest, sectionElement) => {
        const distance = Math.abs(sectionElement.getBoundingClientRect().top - viewportLine);

        if (!closest || distance < closest.distance) {
          return { id: sectionElement.id, distance };
        }

        return closest;
      }, null as { id: string; distance: number } | null);

      return closestSection?.id ?? sectionElements[0].id;
    };

    const updateActiveSection = () => {
      const visibleEntries = sectionElements
        .map(sectionElement => observedSections.get(sectionElement.id))
        .filter(
          (entry): entry is IntersectionObserverEntry => Boolean(entry) && entry.isIntersecting,
        )
        .sort((left, right) => {
          if (right.intersectionRatio !== left.intersectionRatio) {
            return right.intersectionRatio - left.intersectionRatio;
          }

          return Math.abs(left.boundingClientRect.top) - Math.abs(right.boundingClientRect.top);
        });

      const nextSectionId = visibleEntries[0]?.target.id ?? getFallbackSectionId();

      setActiveSectionId(currentSectionId =>
        currentSectionId === nextSectionId ? currentSectionId : nextSectionId,
      );
    };

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          observedSections.set(entry.target.id, entry);
        });

        updateActiveSection();
      },
      {
        root: null,
        rootMargin: '-12% 0px -45% 0px',
        threshold: [0, 0.1, 0.2, 0.35, 0.5, 0.7, 1],
      },
    );

    sectionElements.forEach(sectionElement => {
      observer.observe(sectionElement);
    });

    updateActiveSection();
    window.addEventListener('resize', updateActiveSection);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateActiveSection);
    };
  }, []);

  const handleNavClick = (sectionId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const targetHash = `#${sectionId}`;
    const targetUrl = `${location.pathname}${targetHash}`;
    const sectionElement = document.getElementById(sectionId);

    if (!sectionElement) {
      return;
    }

    if (`${window.location.pathname}${window.location.hash}` !== targetUrl) {
      window.history.replaceState(null, '', targetUrl);
    }

    sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleBackToTopClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (window.location.hash) {
      window.history.replaceState(null, '', location.pathname);
    }

    setActiveSectionId('getting-started');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderTable = (rows: string[][]) => (
    <div className={classes.tableBlock}>
      {rows.map(([field, status, description]) => (
        <div className={classes.tableRow} key={`${field}-${status}`}>
          <div className={classes.tableCell}>
            <div className={classes.fieldName}>{field}</div>
          </div>
          <div className={`${classes.tableCell} ${classes.statusCell}`}>{status}</div>
          <div className={classes.tableCell}>{description}</div>
        </div>
      ))}
    </div>
  );

  const renderBackToTop = () => (
    <div className={classes.backToTopRow}>
      <a href={location.pathname} onClick={handleBackToTopClick} className={classes.backToTop}>
        <NorthIcon /> Back to top
      </a>
    </div>
  );

  return (
    <PageWithHeader
      title="AI Store"
      themeId="home"
      subtitle={
        <DevxBreadCrumb
          routes={[
            { type: 'link', link: '/', text: 'Home' },
            { type: 'link', link: '/ai-store', text: 'AI Store' },
            { type: 'link', link: '/ai-store/agents', text: 'Agents' },
            { type: 'text', link: '', text: 'Guide' },
          ]}
        />
      }
    >
      <Content>
        <div id="top" className={classes.topAnchor} />
        <div className={classes.buttonsWrapper}>
          <BackButton />
        </div>
        <div className={classes.content}>
          <div className={classes.container}>
            <div className={classes.guideLayout}>
              <aside className={classes.sidebar}>
                <Typography className={classes.sidebarTitle}>Guide Contents</Typography>
                <div className={classes.navList}>
                  {GUIDE_ITEMS.map(item => (
                    <a
                      key={item.id}
                      href={`${location.pathname}#${item.id}`}
                      onClick={handleNavClick(item.id)}
                      className={`${classes.navItem} ${
                        activeSectionId === item.id ? classes.activeNavItem : ''
                      }`}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </aside>

              <main className={classes.main}>
                <section id="getting-started" className={classes.section}>
                  <Typography className={classes.title}>What is an AI Agent?</Typography>
                  <Typography className={classes.lead}>
                    <strong>An AI Agent</strong> in the CBRE catalog is a purpose-built
                    digital tool that uses artificial intelligence to help employees
                    complete specific work tasks faster, more consistently, and with less
                    manual effort.
                  </Typography>
                  <Typography className={classes.lead}>
                    Think of it as a smart colleague that has been trained on a
                    particular job: drafting proposals, analysing leases, summarising
                    financial reports, or responding to RFPs. Unlike a general-purpose
                    chatbot, each agent is scoped to a defined use case and validated
                    before it is made available to other teams.
                  </Typography>

                  <div className={classes.benefitsGrid}>
                    {BENEFIT_CARDS.map(card => {
                      const IconComponent = card.icon;
                      return (
                        <div className={classes.benefitCard} key={card.title}>
                          <div className={classes.benefitIconBox}>
                            <IconComponent />
                          </div>
                          <Typography className={classes.benefitTitle}>
                            {card.title}
                          </Typography>
                          <Typography className={classes.benefitText}>
                            {card.description}
                          </Typography>
                        </div>
                      );
                    })}
                  </div>

                  <div className={classes.callout}>
                    <div className={classes.calloutIcon}>
                      <InfoOutlinedIcon />
                    </div>
                    <div className={classes.calloutText}>
                      <strong>Good to know:</strong> You don't need to be a developer to
                      create or use an agent. The catalog is designed for all CBRE
                      employees.
                    </div>
                  </div>

                  {renderBackToTop()}
                </section>

                <section id="creating-agents" className={classes.section}>
                  <Typography className={classes.sectionLabel}>Section 2</Typography>
                  <Typography className={classes.title}>Create an Agent</Typography>
                  <Typography className={classes.lead}>
                    Any CBRE employee can submit a new AI Agent to the catalog. Once
                    submitted, it goes through a review and approval process before being
                    published for others to discover and use.
                  </Typography>

                  <Typography className={classes.sectionHeading}>
                    Click "+ Add New Agent"
                  </Typography>
                  <Typography className={classes.sectionParagraph}>
                    From the catalog home page, click the <strong>+ Add New Agent</strong>{' '}
                    button in the top-right corner to open the submission form.
                  </Typography>

                  <Typography className={classes.sectionHeading}>Fill in the Form</Typography>
                  <Typography className={classes.sectionParagraph}>
                    The form is divided into several sections (detailed in the next
                    section of this guide). Work through each section top to bottom.
                    Required fields are marked with an asterisk (*).
                  </Typography>

                  <Typography className={classes.sectionHeading}>
                    Save as Draft or Submit
                  </Typography>
                  <Typography className={classes.sectionParagraph}>
                    At any point you can click <strong>Save as Draft</strong> to return to
                    the form later. When you are ready, click <strong>Submit for Review</strong>{' '}
                    to send the agent to the approval queue. You will not be able to edit
                    the submission while it is under review.
                  </Typography>

                  <div className={classes.callout}>
                    <div className={classes.calloutIcon}>
                      <InfoOutlinedIcon />
                    </div>
                    <div className={classes.calloutText}>
                      <strong>Before you submit:</strong> Make sure you have your
                      validation evidence ready. A file upload or link is required by the
                      governance process.
                    </div>
                  </div>

                  {renderBackToTop()}
                </section>

                <section id="configuration" className={classes.section}>
                  <Typography className={classes.sectionLabel}>Section 3</Typography>
                  <Typography className={classes.title}>Configuration Guide</Typography>
                  <Typography className={classes.lead}>
                    The submission form walks you through every aspect of your agent. Use
                    the field reference below to understand what each section requires.
                  </Typography>

                  <Typography className={classes.sectionHeading}>Basic Information</Typography>
                  <Typography className={classes.sectionParagraph}>
                    Core identity: what your agent is called and who can see it.
                  </Typography>
                  {renderTable(BASIC_INFORMATION_ROWS)}

                  <Typography className={classes.sectionHeading}>Classification</Typography>
                  <Typography className={classes.sectionParagraph}>
                    Define the nature of the AI technology powering your agent.
                  </Typography>
                  {renderTable(CLASSIFICATION_ROWS)}

                  <Typography className={classes.sectionHeading}>
                    Overview & Capabilities
                  </Typography>
                  <Typography className={classes.sectionParagraph}>
                    Help users quickly understand what the agent can do.
                  </Typography>
                  {renderTable(OVERVIEW_ROWS)}

                  <Typography className={classes.sectionHeading}>Target Audience</Typography>
                  <Typography className={classes.sectionParagraph}>
                    Define who this agent is built for so it appears in the right
                    recommendations.
                  </Typography>
                  {renderTable(TARGET_AUDIENCE_ROWS)}

                  <Typography className={classes.sectionHeading}>
                    What You Provide & What You Get
                  </Typography>
                  <Typography className={classes.sectionParagraph}>
                    Set clear expectations for users about the inputs they need to supply
                    and the outputs they will receive.
                  </Typography>
                  {renderTable(INPUT_OUTPUT_ROWS)}

                  <div className={classes.callout}>
                    <div className={classes.calloutIcon}>
                      <InfoOutlinedIcon />
                    </div>
                    <div className={classes.calloutText}>
                      <strong>Tip:</strong> Clear "What You Provide / What You Get"
                      descriptions are one of the biggest factors in whether colleagues
                      trust and adopt your assistant.
                    </div>
                  </div>

                  <Typography className={classes.sectionHeading}>
                    Trust & Governance
                  </Typography>
                  <Typography className={classes.sectionParagraph}>
                    Capture compliance, data handling, and validation information. This
                    section is critical for the approval process.
                  </Typography>
                  {renderTable(GOVERNANCE_ROWS)}

                  <Typography className={classes.sectionHeading}>Provide Evidence</Typography>
                  <Typography className={classes.sectionParagraph}>
                    Upload supporting documentation or link to it. Reviewers use this to
                    verify the assistant&apos;s quality and compliance.
                  </Typography>
                  {renderTable(EVIDENCE_ROWS)}

                  <Typography className={classes.sectionHeading}>
                    Release Notes (Optional)
                  </Typography>
                  <Typography className={classes.sectionParagraph}>
                    Document what changed in each version. Useful when you update an
                    existing assistant and resubmit it.
                  </Typography>
                  {renderTable(RELEASE_NOTE_ROWS)}

                  {renderBackToTop()}
                </section>

                <section id="using-agents" className={classes.section}>
                  <Typography className={classes.sectionLabel}>Section 4</Typography>
                  <Typography className={classes.title}>Using AI Agents Effectively</Typography>
                  <Typography className={classes.lead}>
                    Get the most value from AI agents by following these best practices
                    and usage guidelines.
                  </Typography>

                  <Typography className={classes.sectionHeading}>
                    Writing Effective Prompts
                  </Typography>
                  <Typography className={classes.sectionParagraph}>
                    The quality of your output depends on the quality of your input.
                    Structure your requests to be:
                  </Typography>
                  <div className={classes.promptList}>
                    {PROMPTING_TIPS.map(([label, text]) => (
                      <div className={classes.promptItem} key={label}>
                        <strong>{label}</strong> - {text}
                      </div>
                    ))}
                  </div>

                  <Typography className={classes.sectionHeading}>Quality Assurance</Typography>
                  <Typography className={classes.sectionParagraph}>
                    Always review the agent&apos;s output before using it in real work.
                    Check for:
                  </Typography>
                  <div className={classes.qaList}>
                    {QA_ITEMS.map(item => (
                      <div className={classes.qaItem} key={item}>{item}</div>
                    ))}
                  </div>

                  <Typography className={classes.sectionHeading}>Providing Feedback</Typography>
                  <Typography className={classes.sectionParagraph}>
                    Help improve agents by providing feedback. Report issues, suggest
                    improvements, and share success stories with your team and the
                    governance team.
                  </Typography>

                  {renderBackToTop()}
                </section>

                <section id="best-practices" className={classes.section}>
                  <Typography className={classes.sectionLabel}>Section 5</Typography>
                  <Typography className={classes.title}>Best Practices & Guidelines</Typography>
                  <Typography className={classes.lead}>
                    Follow these proven practices to maximize the value and impact of AI
                    agents across your organization.
                  </Typography>

                  {BEST_PRACTICES.map(([title, text]) => (
                    <div className={classes.practiceItem} key={title}>
                      <Typography className={classes.practiceTitle}>{title}</Typography>
                      <Typography className={classes.practiceText}>{text}</Typography>
                    </div>
                  ))}

                  {renderBackToTop()}
                </section>

                <section id="faq" className={classes.section}>
                  <Typography className={classes.sectionLabel}>Section 6</Typography>
                  <Typography className={classes.title}>Frequently Asked Questions</Typography>

                  <div className={classes.faqList}>
                    {FAQ_ITEMS.map(([question, answer]) => (
                      <div className={classes.faqItem} key={question}>
                        <Typography className={classes.faqQuestion}>{question}</Typography>
                        <Typography className={classes.faqAnswer}>{answer}</Typography>
                      </div>
                    ))}
                  </div>

                  {renderBackToTop()}
                </section>
              </main>
            </div>
          </div>
        </div>
      </Content>
    </PageWithHeader>
  );
};

export default AiStoreGuidePage;